import { Injectable } from "@angular/core";
import { map, of, switchMap } from "rxjs";
import { SiteInformation } from "src/app/_models/site-information.model";
import { User } from "src/app/_models/user-models/user";
import { SiteService } from "../site/site.services";

export interface UserScopeResult {
  customers: SiteInformation[];
  sites: SiteInformation[];
  showCustomerDropdown: boolean;
  isViewer: boolean;
}

@Injectable({ providedIn: "root" })
export class CustomerSiteScopeService {
  constructor(private siteService: SiteService) {}

  resolveSitesByUserScope(user: User): any {
    const orgType = user.OrgType?.toUpperCase();
    const jobType = user.JobType?.toUpperCase();

    const isViewer = orgType === "VIEWER";
    const showCustomerDropdown = orgType !== "HOSPITAL" && jobType !== "D";

    // SP
    if (orgType === "SP") {
      return this.siteService.getCustomerByOrgParentId(user.OrgId).pipe(
        switchMap((customers) =>
          this.siteService.getHospitalByOrgParentIdList(customers).pipe(
            map((sites) => ({
              customers,
              sites,
              showCustomerDropdown,
              isViewer,
            })),
          ),
        ),
      );
    }

    // CUSTOMER NON D
    if (orgType === "CUSTOMER" && jobType !== "D") {
      return this.siteService.getSiteDetail(user.OrgId).pipe(
        switchMap((customer) =>
          this.siteService.getHospitalByOrgParentIdList([customer]).pipe(
            map((sites) => ({
              customers: [customer],
              sites,
              showCustomerDropdown,
              isViewer,
            })),
          ),
        ),
      );
    }

    // CUSTOMER D
    if (orgType === "CUSTOMER" && jobType === "D") {
      return this.siteService.getSiteDetail(user.OrgId).pipe(
        switchMap((customer) =>
          this.siteService
            .getHospitalByContactOrgParentIdList([customer], user.EmpId)
            .pipe(
              map((sites) => ({
                customers: [customer],
                sites,
                showCustomerDropdown,
                isViewer,
              })),
            ),
        ),
      );
    }

    // HOSPITAL
    if (orgType === "HOSPITAL") {
      const site = new SiteInformation(user.OrgName, user.OrgParentId);
      site.OrgParentId = user.OrgParentId;

      return of({
        customers: [],
        sites: [site],
        showCustomerDropdown: false,
        isViewer: false,
      });
    }

    // VIEWER
    if (orgType === "VIEWER") {
      const customer = new SiteInformation(user.OrgName, user.OrgParentId);

      return this.siteService.getHospitalByOrgParentIdList([customer]).pipe(
        map((sites) => ({
          customers: [customer],
          sites,
          showCustomerDropdown: true,
          isViewer: true,
        })),
      );
    }

    return of({
      customers: [],
      sites: [],
      showCustomerDropdown: false,
      isViewer: false,
    });
  }

  resolveSitesByCustomersScope(customers: SiteInformation[], user: User) {
    const orgType = user.OrgType?.toUpperCase();
    const jobType = user.JobType?.toUpperCase();

    // CUSTOMER D (special case)
    if (orgType === "CUSTOMER" && jobType !== "D") {
      return this.siteService.getHospitalByContactOrgParentIdList(
        customers,
        user.EmpId,
      );
    }

    // All others use normal hospital resolver
    return this.siteService.getHospitalByOrgParentIdList(customers);
  }
}
